import { Injectable, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { JwtHelperService } from '@auth0/angular-jwt'
import {
    AccessTokenPayload,
    AuthState,
    LoginResponse,
    SignupInput,
} from '@mattrika/benefit-model'
import { getUserInitials } from '@mattrika/utils-local'
import { WA_WINDOW } from '@ng-web-apis/common'
import {
    LocalStorageService,
    TokenStorageService,
} from '@touhidrahman/ngx-common'
import { SimpleStore } from '@touhidrahman/simple-store'
import destr from 'destr'
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs'
import { AuthApiService } from './auth-api.service'

export const initialAuthState: AuthState = {
    isLoggedIn: false,
    tokenPayload: null,
    userDetails: null,
    isSuperAdmin: false,
}

@Injectable({
    providedIn: 'root',
})
export class AuthStateService extends SimpleStore<AuthState> {
    private jwtHelper = new JwtHelperService()
    private tokenStorageService = inject(TokenStorageService)
    private authApiService = inject(AuthApiService)
    private router = inject(Router)
    private localStorageService = inject(LocalStorageService)
    private windowRef = inject(WA_WINDOW)
    private activeRefresh$: Observable<string | null> | null = null

    readonly authState = toSignal(this.selectAll(), {
        initialValue: initialAuthState,
    })
    readonly userDetailsSignal = toSignal(this.select('userDetails'), {
        initialValue: null,
    })
    readonly isLoggedInSignal = toSignal(this.select('isLoggedIn'), {
        initialValue: false,
    })
    readonly isSuperAdminSignal = toSignal(this.select('isSuperAdmin'), {
        initialValue: false,
    })

    readonly userSignal = computed(() => this.userDetailsSignal()?.user ?? null)
    readonly isOwnerSignal = computed(() => Boolean(this.userSignal()?.isOwner))
    readonly userNameSignal = computed(() => {
        const user = this.userSignal()
        if (!user) return null
        const { firstName, lastName } = user
        if (!firstName && !lastName) return null
        return [firstName, lastName].filter(Boolean).join(' ')
    })
    readonly userEmailSignal = computed(() => {
        return (
            this.userDetailsSignal()?.userIdentifiers?.find(
                (x) => x.identifierType === 'email',
            )?.identifierValue ?? null
        )
    })
    readonly userRoleSignal = computed<'Admin' | 'Vendor' | 'Client'>(() => {
        const details = this.userDetailsSignal()
        if (details?.isSuperAdmin) return 'Admin'

        const orgType = (details?.organizationType ?? '').toLowerCase()
        const roleName = (details?.role?.name ?? '').toLowerCase()
        const groupName = (details?.group?.name ?? '').toLowerCase()

        if (
            orgType.includes('admin') ||
            roleName.includes('admin') ||
            groupName.includes('admin')
        ) {
            return 'Admin'
        }
        if (
            orgType.includes('vendor') ||
            roleName.includes('vendor') ||
            groupName.includes('vendor') ||
            Boolean(details?.vendor)
        ) {
            return 'Vendor'
        }
        if (
            orgType.includes('client') ||
            roleName.includes('client') ||
            groupName.includes('client') ||
            Boolean(details?.client)
        ) {
            return 'Client'
        }

        return 'Client'
    })

    readonly companyNameSignal = computed<string>(() => {
        const details = this.userDetailsSignal()
        return (
            details?.vendor?.name ||
            details?.client?.name ||
            details?.organization?.name ||
            details?.group?.name ||
            ''
        )
    })

    readonly userInitialsSignal = computed(() => {
        const user = this.userSignal()
        return getUserInitials({
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: this.userEmailSignal(),
            role: this.userRoleSignal(),
        })
    })

    constructor() {
        super(initialAuthState)
        this.initAuthFromStorage()
    }

    getUser() {
        return this.getState().userDetails?.user
    }

    getUserName() {
        const user = this.getState().userDetails?.user

        if (!user) return null

        const { firstName, lastName } = user

        if (!firstName && !lastName) return null
        return firstName + ' ' + lastName
    }

    getUserEmail() {
        return (
            this.getState().userDetails?.userIdentifiers?.filter(
                (x) => x.identifierType === 'email',
            )[0]?.identifierValue ?? null
        )
    }

    getRole() {
        return this.getState().userDetails?.role ?? null
    }

    getUserId(): string | null {
        return this.getState().userDetails?.user?.id ?? null
    }

    getGroup() {
        return this.getState().userDetails?.group
    }

    getGroupType() {
        return this.getState().userDetails?.organizationType
    }

    hasPermission(permission: string) {
        return (
            this.getState().userDetails?.permissions?.enabled ?? []
        ).includes(permission)
    }

    isSuperAdmin(): boolean {
        return this.getState().isSuperAdmin
    }

    isOwner(): boolean {
        return Boolean(this.getState().userDetails?.user?.isOwner)
    }

    isLoggedIn(): boolean {
        return this.getState().isLoggedIn
    }

    login(identifier: string, password: string) {
        this.tokenStorageService.clear()
        return this.authApiService.login(identifier, password).pipe(
            map(({ data }) => {
                if (data) {
                    this.setStateAfterLogin(data)
                }

                return data
            }),
        )
    }

    register(signupInput: SignupInput) {
        return this.authApiService.register(signupInput)
    }

    initAuthFromStorage() {
        const accessToken = this.tokenStorageService.getAccessToken()
        const refreshToken = this.tokenStorageService.getRefreshToken()

        if (!accessToken || !refreshToken) {
            this.tokenStorageService.clear()
            return
        }

        if (this.isTokenExpired(refreshToken)) {
            this.tokenStorageService.clear()
            this.localStorageService.removeItem('userDetails')
            return
        }

        const userDetails = destr<LoginResponse>(
            this.localStorageService.getItem('userDetails'),
        )
        const tokenPayload = userDetails
            ? this.mapTokenPayload(userDetails)
            : null

        this.setState({
            isLoggedIn: true,
            userDetails,
            tokenPayload,
            isSuperAdmin: userDetails?.isSuperAdmin ?? false,
        })
    }

    refreshAccessToken(): Observable<string | null> {
        if (this.activeRefresh$) {
            return this.activeRefresh$
        }

        const refreshToken = this.tokenStorageService.getRefreshToken()
        if (!refreshToken) {
            return of(null)
        }

        if (this.isTokenExpired(refreshToken)) {
            return of(null)
        }

        this.activeRefresh$ = this.authApiService
            .refreshAccessToken(refreshToken)
            .pipe(
                map((res) => {
                    const data = res.data
                    if (data) {
                        this.updateSessionAfterRefresh(data)
                    }
                    return data?.accessToken ?? null
                }),
                catchError((err) => {
                    if (err?.status === 400 || err?.status === 401) {
                        this.tokenStorageService.clear()
                    }
                    return of(null)
                }),
                finalize(() => {
                    this.activeRefresh$ = null
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            )

        return this.activeRefresh$
    }

    setStateAfterLogin(loginResponse: LoginResponse) {
        const tokenPayload = this.mapTokenPayload(loginResponse)
        this.setState({
            isLoggedIn: true,
            tokenPayload,
            userDetails: loginResponse,
            isSuperAdmin: loginResponse.isSuperAdmin ?? false,
        })
        this.saveToStorage(loginResponse)
    }

    mapTokenPayload(data: LoginResponse) {
        if (!data?.accessToken) return null
        const decoded = this.jwtHelper.decodeToken<AccessTokenPayload>(
            data.accessToken,
        ) as AccessTokenPayload

        return decoded
    }

    logout(redirectPath = '', reload = false) {
        this.reset()
        this.tokenStorageService.clear()
        this.localStorageService.removeItem('userDetails')
        this.router.navigate([redirectPath])
        if (reload) this.windowRef.location.reload()
    }

    isTokenExpired(token: string | null, offsetSeconds = 0): boolean {
        if (!token) return true

        try {
            const decoded: { exp?: number } | null =
                this.jwtHelper.decodeToken(token)
            if (!decoded?.exp) return true

            const expInSeconds =
                decoded.exp > 1e12
                    ? Math.floor(decoded.exp / 1000)
                    : decoded.exp

            const nowInSeconds = Math.floor(Date.now() / 1000)
            return expInSeconds <= nowInSeconds + offsetSeconds
        } catch {
            return true
        }
    }

    isAccessTokenExpiringIn(bufferSeconds: number): boolean {
        const token = this.tokenStorageService.getAccessToken()
        return this.isTokenExpired(token, bufferSeconds)
    }

    private saveToStorage(res: LoginResponse) {
        this.localStorageService.setItem('userDetails', JSON.stringify(res))
        this.saveTokensToStorage(res)
    }

    private saveTokensToStorage(res: Partial<LoginResponse>) {
        if (res.accessToken) {
            this.tokenStorageService.saveAccessToken(res.accessToken)
        }
        if (res.refreshToken) {
            this.tokenStorageService.saveRefreshToken(res.refreshToken)
        }
    }

    private updateSessionAfterRefresh(res: Partial<LoginResponse>) {
        this.saveTokensToStorage(res)

        const currentState = this.getState()
        const existingUserDetails = currentState.userDetails
        if (existingUserDetails && res.accessToken) {
            const updatedUserDetails: LoginResponse = {
                ...existingUserDetails,
                accessToken: res.accessToken,
                refreshToken:
                    res.refreshToken || existingUserDetails.refreshToken,
            }
            const tokenPayload = this.mapTokenPayload(updatedUserDetails)
            this.setState({
                userDetails: updatedUserDetails,
                tokenPayload,
            })
            this.localStorageService.setItem(
                'userDetails',
                JSON.stringify(updatedUserDetails),
            )
        }
    }
}

export function injectCurrentUser() {
    const auth = inject(AuthStateService)
    return {
        auth,
        isLoggedIn: auth.isLoggedInSignal,
        isSuperAdmin: auth.isSuperAdminSignal,
        isOwner: auth.isOwnerSignal,
        user: auth.userSignal,
        userName: auth.userNameSignal,
        userEmail: auth.userEmailSignal,
        userRole: auth.userRoleSignal,
        userInitials: auth.userInitialsSignal,
        companyName: auth.companyNameSignal,
        logout: (redirectPath = '/login') => auth.logout(redirectPath),
    }
}
